import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Star, Copy, Archive, ArchiveRestore, FolderOpen, X, History, Download, Play } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/AuthContext";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS } from "./shared";

const COLORS = ["#C6FF3C", "#FFD23C", "#FF6B35", "#4ECDC4", "#A78BFA", "#F5F3F0"];
const TYPES = ["BXG", "BPT", "BG", "SC", "YTH"];
const RECENT_DAYS = 14;

function isRecent(it) {
  const ts = it.last_used_at || it.updated_at;
  return ts && (Date.now() - new Date(ts).getTime()) < RECENT_DAYS * 86400000;
}

/* Metadata-formulier (nieuw / bewerken) — module-scope voor stabiele focus. */
function ItemForm({ item, folders, onSave, onClose, busy }) {
  const [f, setF] = useState(item);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [tagInput, setTagInput] = useState("");
  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !(f.tags || []).includes(t)) set("tags", [...(f.tags || []), t]);
    setTagInput("");
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-6 md:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="engine-item-form">
        <div className="chip text-[#A3A3A3]">// {item.id ? "TRAINING BEWERKEN" : "NIEUWE TRAINING"}</div>
        <div className="space-y-5 mt-5">
          <div><label className="chip text-[#A3A3A3]">Naam*</label>
            <input value={f.name} onChange={(e) => set("name", e.target.value)} className="editorial-input dark w-full" data-testid="engine-f-name" /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="chip text-[#A3A3A3]">Type</label>
              <select value={f.type} onChange={(e) => set("type", e.target.value)} className="editorial-input dark w-full" data-testid="engine-f-type">
                {TYPES.map((t) => <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>)}
              </select></div>
            <div><label className="chip text-[#A3A3A3]">Level</label>
              <select value={f.level} onChange={(e) => set("level", e.target.value)} className="editorial-input dark w-full" data-testid="engine-f-level">
                {["BEGINNER", "INTERMEDIATE", "ADVANCED"].map((l) => <option key={l} value={l} className="bg-[#0A0A0A]">{l}</option>)}
              </select></div>
            <div><label className="chip text-[#A3A3A3]">Duur (min)</label>
              <input type="number" min="5" value={f.planned_duration} onChange={(e) => set("planned_duration", parseInt(e.target.value) || 0)} className="editorial-input dark w-full" data-testid="engine-f-duration" /></div>
          </div>
          <div><label className="chip text-[#A3A3A3]">Map</label>
            <input value={f.folder} onChange={(e) => set("folder", e.target.value)} className="editorial-input dark w-full" placeholder="Bv. Techniek, Conditie…" list="engine-folders" data-testid="engine-f-folder" />
            <datalist id="engine-folders">{folders.map((x) => <option key={x} value={x} />)}</datalist></div>
          <div><label className="chip text-[#A3A3A3]">Kleur</label>
            <div className="flex gap-2 mt-1">
              {COLORS.map((c) => (
                <button type="button" key={c} onClick={() => set("color", c)}
                  className={`w-9 h-9 border-2 transition-transform ${f.color === c ? "scale-110 border-white" : "border-transparent"}`}
                  style={{ background: c }} title={c} data-testid={`engine-f-color-${c.replace("#", "")}`} />
              ))}
            </div></div>
          <div><label className="chip text-[#A3A3A3]">Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(f.tags || []).map((t) => (
                <span key={t} className="chip px-2 py-1 border border-[#F5F3F0]/20 text-[#DDD6CB] inline-flex items-center gap-1.5">
                  {t} <button type="button" onClick={() => set("tags", f.tags.filter((x) => x !== t))} className="text-[#4A4A4A] hover:text-red-400" data-testid={`engine-f-tag-del-${t}`}><X size={11} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                className="editorial-input dark flex-1" placeholder="Tag toevoegen + Enter" data-testid="engine-f-tag-input" />
              <button type="button" onClick={addTag} className="chip px-3 py-2 border border-[#F5F3F0]/25 text-[#A3A3A3]" data-testid="engine-f-tag-add">+</button>
            </div></div>
        </div>
        <div className="flex gap-3 mt-8">
          <button onClick={() => onSave(f)} disabled={busy || !f.name.trim()} className="btn-light flex-1 justify-center disabled:opacity-40" data-testid="engine-f-save">
            {busy ? "Opslaan…" : "Opslaan"}
          </button>
          <button onClick={onClose} disabled={busy} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#A3A3A3]" data-testid="engine-f-cancel">Annuleer</button>
        </div>
      </div>
    </div>
  );
}

export default function JaceEngine() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all"); // all | fav | recent | archived | folder:<naam>
  const [type, setType] = useState("");
  const [tag, setTag] = useState("");
  const [edit, setEdit] = useState(null); // item in metadata-form
  const [versionsFor, setVersionsFor] = useState(null); // {item, versions}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const archived = view === "archived" ? "1" : "0";
    api.get("/jace/library", { params: { archived, fav: view === "fav" ? "1" : "", folder: view.startsWith("folder:") ? view.slice(7) : "", q, type, tag } })
      .then(({ data }) => setData(data)).catch(() => setData({ items: [], folders: [], tags: [] }));
  }, [view, q, type, tag]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => {
    let list = data?.items || [];
    if (view === "recent") list = list.filter(isRecent);
    return [...list].sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
  }, [data, view]);

  const save = async (f) => {
    setBusy(true);
    try {
      if (f.id) {
        await api.put(`/jace/library/${f.id}`, f);
        toast.success("Training opgeslagen");
      } else {
        await api.post("/jace/library", f);
        toast.success("Training aangemaakt — open de Builder om de inhoud te maken");
      }
      setEdit(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt"); }
    setBusy(false);
  };

  const act = async (id, action, body = {}) => {
    try {
      await api.post(`/jace/library/${id}/${action}`, body);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Actie mislukt"); }
  };

  const showVersions = async (item) => {
    try {
      const { data } = await api.get(`/jace/library/${item.id}`);
      setVersionsFor(data);
    } catch { toast.error("Kon versies niet laden"); }
  };

  const downloadPdf = async (it) => {
    try {
      const res = await api.get(`/jace/library/${it.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jace-${(it.name || "training").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF gedownload");
    } catch { toast.error("PDF downloaden mislukt"); }
  };

  const restoreVersion = async (version) => {
    if (!window.confirm(`Versie ${version} herstellen? De huidige inhoud wordt zelf bewaard als versie.`)) return;
    try {
      await api.post(`/jace/library/${versionsFor.id}/restore-version`, { version });
      toast.success(`Hersteld vanuit ${version}`);
      setVersionsFor(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Herstellen mislukt"); }
  };

  const SIDE = [
    ["all", "Alle trainingen"], ["fav", "Favorieten"], ["recent", `Recent (${RECENT_DAYS}d)`], ["archived", "Archief"],
  ];

  return (
    <JaceLayout>
      <div className="max-w-6xl" data-testid="jace-engine">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-tag section-tag-dark mb-3">// COACHING ENGINE</div>
            <h2 className="font-display uppercase text-4xl md:text-5xl">Trainings-<br />bibliotheek.</h2>
            <p className="text-[#A3A3A3] text-sm mt-3 max-w-lg">Herbruikbare trainingen zonder datum. Bouw een keer, plan keer op keer in via de Session Builder.</p>
          </div>
          <button onClick={() => setEdit({ name: "", type: "BXG", level: "BEGINNER", planned_duration: 60, folder: "", color: COLORS[0], tags: [] })}
            className="btn-light" data-testid="engine-new">
            <Plus size={14} /> Nieuwe training
          </button>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar: views + mappen */}
          <div className="lg:col-span-1">
            <div className="flex lg:flex-col flex-wrap gap-1.5" data-testid="engine-views">
              {SIDE.map(([k, lbl]) => (
                <button key={k} onClick={() => setView(k)}
                  className={`text-left px-3 py-2 text-sm border-l-2 transition-colors ${view === k ? "text-[#F5F3F0] bg-[#F5F3F0]/10" : "text-[#A3A3A3] hover:text-[#F5F3F0] border-transparent"}`}
                  style={view === k ? { borderColor: JACE } : {}} data-testid={`engine-view-${k}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {(data?.folders || []).length > 0 && (
              <div className="mt-6">
                <div className="chip text-[#4A4A4A] mb-2">— MAPPEN</div>
                <div className="flex lg:flex-col flex-wrap gap-1.5" data-testid="engine-folders">
                  {data.folders.map((f) => (
                    <button key={f} onClick={() => setView(`folder:${f}`)}
                      className={`text-left px-3 py-2 text-sm border-l-2 transition-colors inline-flex items-center gap-2 ${view === `folder:${f}` ? "text-[#F5F3F0] bg-[#F5F3F0]/10" : "text-[#A3A3A3] hover:text-[#F5F3F0] border-transparent"}`}
                      style={view === `folder:${f}` ? { borderColor: JACE } : {}} data-testid={`engine-folder-${f}`}>
                      <FolderOpen size={12} /> {f}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Hoofd: zoeken + filters + kaarten */}
          <div className="lg:col-span-3">
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of tag…" className="editorial-input dark pl-6 w-full" data-testid="engine-search" />
              </div>
              <select value={type} onChange={(e) => setType(e.target.value)} className="editorial-input dark md:w-40" data-testid="engine-type-filter">
                <option value="" className="bg-[#0A0A0A]">Alle types</option>
                {TYPES.map((t) => <option key={t} value={t} className="bg-[#0A0A0A]">{t} — {TYPE_LABELS[t] || t}</option>)}
              </select>
            </div>
            {(data?.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-6" data-testid="engine-tags">
                {data.tags.map((t) => (
                  <button key={t} onClick={() => setTag(tag === t ? "" : t)}
                    className={`chip px-2.5 py-1 border transition-colors ${tag === t ? "border-[#C6FF3C] text-[#C6FF3C]" : "border-[#F5F3F0]/15 text-[#A3A3A3] hover:text-[#F5F3F0]"}`}
                    data-testid={`engine-tag-${t}`}>{t}</button>
                ))}
              </div>
            )}

            {data === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : items.length === 0 ? (
              <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#4A4A4A]" data-testid="engine-empty">
                {view === "archived" ? "Geen gearchiveerde trainingen." : "Nog geen trainingen — maak er een of bewaar een sessie naar de Engine."}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="engine-grid">
                {items.map((it) => (
                  <div key={it.id} className="border border-[#F5F3F0]/15 hover:border-[#F5F3F0]/30 transition-colors" data-testid={`engine-card-${it.id}`}>
                    <div className="h-1.5" style={{ background: it.color || COLORS[0] }} />
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <button onClick={() => nav(`/jace/sessions/new?engine=${it.id}`)} className="font-display uppercase text-lg leading-tight text-left hover:underline underline-offset-4" data-testid={`engine-open-${it.id}`}>
                          {it.name}
                        </button>
                        <button onClick={() => act(it.id, "favorite", { favorite: !it.favorite })} className={`shrink-0 transition-colors ${it.favorite ? "" : "text-[#4A4A4A] hover:text-[#F5F3F0]"}`}
                          style={it.favorite ? { color: JACE } : {}} title="Favoriet" data-testid={`engine-fav-${it.id}`}>
                          <Star size={16} fill={it.favorite ? "currentColor" : "none"} />
                        </button>
                      </div>
                      <div className="chip text-[#A3A3A3] mt-2">{it.type} · {it.level} · {it.planned_duration} MIN · {it.version}</div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {it.folder && <span className="chip px-2 py-0.5 border border-[#F5F3F0]/15 text-[#A3A3A3]">{it.folder}</span>}
                        {(it.tags || []).map((t) => <span key={t} className="chip px-2 py-0.5 border border-[#F5F3F0]/10 text-[#4A4A4A]">{t}</span>)}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-[#F5F3F0]/10">
                        <button onClick={() => nav(`/jace/play/engine/${it.id}`)} className="chip inline-flex items-center gap-1 transition-colors" style={{ background: JACE, color: "#0A0A0A" }} title="Speel af in de Session Player" data-testid={`engine-play-${it.id}`}><Play size={11} /> Play</button>
                        <button onClick={() => setEdit(it)} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors" data-testid={`engine-edit-${it.id}`}>Bewerk info</button>
                        <button onClick={() => act(it.id, "duplicate")} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1 transition-colors" data-testid={`engine-dup-${it.id}`}><Copy size={11} /> Dupliceer</button>
                        <button onClick={() => showVersions(it)} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1 transition-colors" data-testid={`engine-versions-${it.id}`}><History size={11} /> Versies</button>
                        <button onClick={() => downloadPdf(it)} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1 transition-colors" title="Download als PDF" data-testid={`engine-pdf-${it.id}`}><Download size={11} /> PDF</button>
                        <button onClick={() => act(it.id, "archive", { archived: view !== "archived" })} className="chip text-[#A3A3A3] hover:text-red-400 inline-flex items-center gap-1 transition-colors" data-testid={`engine-archive-${it.id}`}>
                          {view === "archived" ? <><ArchiveRestore size={11} /> Herstel</> : <><Archive size={11} /> Archiveer</>}
                        </button>
                        <span className="chip text-[#4A4A4A] ml-auto">{it.usage_count || 0}× gebruikt</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {edit && <ItemForm item={edit} folders={data?.folders || []} onSave={save} onClose={() => setEdit(null)} busy={busy} />}

      {versionsFor && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setVersionsFor(null)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-md w-full p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="engine-versions-modal">
            <div className="chip text-[#A3A3A3]">// VERSIES — {versionsFor.name}</div>
            <div className="chip mt-2" style={{ color: JACE }}>Huidig: {versionsFor.version}</div>
            <div className="mt-4 line-t line-dark max-h-80 overflow-y-auto">
              {(versionsFor.versions || []).length === 0 ? (
                <div className="chip text-[#4A4A4A] py-4">Nog geen eerdere versies — die ontstaan bij elke inhoudelijke wijziging.</div>
              ) : [...(versionsFor.versions || [])].reverse().map((v) => (
                <div key={v.version + v.at} className="line-b line-dark py-3 flex items-center gap-3">
                  <span className="chip text-[#DDD6CB]">{v.version}</span>
                  <span className="chip text-[#4A4A4A] flex-1">{new Date(v.at).toLocaleString("nl-NL")} · {v.by}</span>
                  <button onClick={() => restoreVersion(v.version)} className="chip px-3 py-1.5 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-[#C6FF3C] hover:border-[#C6FF3C] transition-colors" data-testid={`engine-restore-${v.version}`}>Herstel</button>
                </div>
              ))}
            </div>
            <button onClick={() => setVersionsFor(null)} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#A3A3A3] mt-6" data-testid="engine-versions-close">Sluiten</button>
          </div>
        </div>
      )}
    </JaceLayout>
  );
}
